// PDF Configuration and Utilities
// Shared configuration for all PDF generators

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Extend jsPDF type globally
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable;
    lastAutoTable: {
      finalY: number;
    };
  }
}

// Export configured jsPDF class
export { jsPDF };

// Export common table configurations
export const defaultAutoTableConfig = {
  theme: 'grid' as const,
  styles: {
    fontSize: 10,
    cellPadding: 3,
    font: 'helvetica',
    textColor: [0, 0, 0],
    lineColor: [200, 200, 200],
    lineWidth: 0.1,
  },
  headStyles: {
    fillColor: [66, 139, 202],
    textColor: [255, 255, 255],
    fontStyle: 'bold' as const,
    halign: 'center' as const,
  },
  bodyStyles: {
    halign: 'left' as const,
  },
  alternateRowStyles: {
    fillColor: [245, 245, 245],
  },
  margin: { 
    left: 20, 
    right: 20,
    top: 10,
    bottom: 10,
  },
  columnStyles: {
    0: { cellWidth: 'auto' },
    1: { cellWidth: 'auto' },
  },
};

// Helper function to safely convert values to strings
export const safeToString = (value: any, defaultValue: string = 'N/A'): string => {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return defaultValue;
    }
  }
  
  return String(value);
};

// Export for backward compatibility
export { safePdfText } from './pdfConfigEnhanced';

// Helper to add a standard header to PDF
export const addPDFHeader = (
  doc: jsPDF, 
  title: string, 
  subtitle?: string
): number => {
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(title, doc.internal.pageSize.width / 2, 20, { align: 'center' });
  
  let currentY = 30;
  
  if (subtitle) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, doc.internal.pageSize.width / 2, currentY, { align: 'center' });
    currentY += 10;
  }
  
  return currentY;
};

// Helper to add page numbers
export const addPageNumbers = (doc: jsPDF): void => {
  const pageCount = doc.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }
};

// Helper for better error handling
export const generatePDFSafely = (generatorFn: () => void, defaultFileName: string = 'report.pdf'): void => {
  try {
    generatorFn();
  } catch (error) {
    console.error('PDF generation failed:', error);
    // Create a simple error PDF
    const errorDoc = new jsPDF();
    errorDoc.setFontSize(14);
    errorDoc.setTextColor(255, 0, 0);
    errorDoc.text('Error generating PDF report', 20, 20);
    errorDoc.setFontSize(10);
    errorDoc.setTextColor(0, 0, 0);
    errorDoc.text(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`, 20, 30);
    errorDoc.text('Please contact support if this issue persists.', 20, 40);
    errorDoc.save(`error_${defaultFileName}`);
  }
};
